import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { NextRequest } from 'next/server';
import { UIPost, FacebookPageData, FacebookAdAccount } from '@/types/social';
import { fetchWithCsrf } from '@/lib/csrf-utils';
import logger from '@/lib/logger';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface UserPlan {
  type: string | null;
  status: string;
}

// Plan fetcher for SWR
export const planFetcher = async (url: string) => {
  const response = await fetchWithCsrf(url);
  if (!response.ok) {
    throw new Error('Failed to fetch user plan');
  }
  const data = await response.json();
  return data.plan;
};

// Instagram fetcher for SWR
export const instagramFetcher = async (url: string) => {
  const response = await fetchWithCsrf(url);
  const data: ApiResponse<UIPost[]> = await response.json();
  
  if (!data.success || !Array.isArray(data.data)) {
    logger.warn('Instagram API response invalid:', data);
    return [];
  }

  return data.data.map(post => ({
    ...post,
    platform: 'instagram' as const,
    comments: post.comments.map(comment => ({
      ...comment,
      commentType: comment.commentType || 'ig_org_ig'
    }))
  }));
};

// Facebook fetcher for SWR
export const facebookFetcher = async (url: string) => {
  const response = await fetchWithCsrf(url);
  const data: ApiResponse<FacebookPageData[]> = await response.json();
  
  if (!data.success || !Array.isArray(data.data)) return [];
  
  return data.data.flatMap(page => 
    page.posts.map(post => {
      const rawMedia = post.media as any;
      const thumbnailUrl = rawMedia?.media_type === 'VIDEO' ? 
        rawMedia?.media?.image?.src || post.thumbnail_url : undefined;

      return {
        id: post.id,
        platform: 'facebook' as const,
        caption: post.message || '',
        media_type: post.media?.media_type || '',
        media_url: thumbnailUrl || post.media?.url || '',
        permalink: post.permalink_url,
        thumbnail_url: thumbnailUrl,
        timestamp: post.created_time,
        username: post.pageName || page.pageName || 'Unknown Page',
        media: post.media ? {
          media_type: post.media.media_type,
          url: thumbnailUrl || post.media.url,
          title: post.media.title
        } : undefined,
        comments: post.comments.map(comment => ({
          ...comment,
          commentType: comment.commentType || 'fb_org',
          pageId: page.pageId
        }))
      };
    })
  );
};

// Ads fetcher for SWR
export const adsFetcher = async (url: string) => {
  const response = await fetchWithCsrf(url);
  const data: ApiResponse<FacebookAdAccount[]> = await response.json();
  
  if (!data.success || !Array.isArray(data.data)) return [];

  return data.data.flatMap(account => 
    account.ads.map(ad => {
      const mediaInfo = ad.creative?.object_story_spec?.video_data ? {
        type: 'VIDEO',
        url: ad.creative.object_story_spec.video_data.thumbnail_url || ''
      } : ad.creative?.object_story_spec?.link_data?.picture ? {
        type: 'IMAGE',
        url: ad.creative.object_story_spec.link_data.picture
      } : ad.creative?.object_story_spec?.photo_data ? {
        type: 'IMAGE',
        url: ad.creative.object_story_spec.photo_data.url
      } : { type: '', url: '' };

      return {
        id: ad.id,
        platform: 'ads' as const,
        caption: ad.name || '',
        media_type: mediaInfo.type,
        media_url: mediaInfo.url,
        permalink: '',
        timestamp: ad.created_time || new Date().toISOString(),
        username: account.name || 'Unknown Account',
        comments: ad.comments.map(comment => ({
          ...comment,
          commentType: comment.commentType,
          adId: ad.id,
          pageId: ad.creative?.object_story_spec?.page_id,
          hidden: comment.hidden
        }))
      };
    })
  );
};

// LinkedIn fetcher for SWR
export const linkedInFetcher = async (url: string) => {
  const response = await fetchWithCsrf(url);
  const data: ApiResponse<UIPost[]> = await response.json();
  
  if (!data.success || !Array.isArray(data.data)) {
    logger.warn('LinkedIn API response invalid:', data);
    return [];
  }

  return data.data.map(post => ({
    ...post,
    platform: 'linkedin' as const,
    comments: post.comments.map(comment => ({
      ...comment,
      commentType: 'linkedin' as const
    }))
  }));
};


// Helper function to determine if a comment is an organic Instagram comment
export function isOrganicInstagramComment(commentType: string): boolean {
  return commentType === 'ig_org_ig' || commentType === 'ig_org_fb';
}

// Helper function to determine if a comment is an ad comment
export function isAdComment(commentType: string): boolean {
  return commentType === 'ig_ad' || commentType === 'fb_ad';
}

// Helper function to get the appropriate endpoint for comment operations
export function getCommentEndpoint(commentType: string, operation: 'reply' | 'hide' | 'like' | 'delete'): string {
  if (isOrganicInstagramComment(commentType)) {
    return `/api/instagram/comments/${operation}`;
  } else if (isAdComment(commentType)) {
    return `/api/facebook/ads/comments/${operation}`;
  } else if (commentType === 'linkedin') {
    return `/api/linkedin/comments/${operation}`;
  } else if (commentType === 'youtube') {
    return `/api/youtube/comments/${operation}`;
  } else {
    return `/api/facebook/comments/${operation}`;
  }
}

// YouTube API response type
interface YouTubeApiResponse extends ApiResponse<UIPost[]> {
  needsReconnect?: boolean;
}

// YouTube fetcher for SWR - detailed version that includes needsReconnect status
export const youtubeFetcher = async (url: string): Promise<YouTubeApiResponse> => {
  const response = await fetchWithCsrf(url);
  const data = await response.json() as YouTubeApiResponse;
  
  if (!data.success || !Array.isArray(data.data)) {
    logger.warn('YouTube API response invalid:', data);
    return {
      success: false,
      data: [],
      error: data.error || 'Invalid YouTube API response',
      needsReconnect: data.needsReconnect
    };
  }

  // Map the posts and ensure the correct platform type
  const mappedData = data.data.map(post => ({
    ...post,
    platform: 'youtube' as const,
    comments: post.comments.map(comment => ({
      ...comment,
      commentType: 'youtube' as const
    }))
  }));

  return {
    ...data,
    data: mappedData
  };
};

// Simple YouTube fetcher compatible with general-purpose components like AllFeed
// that only need the post data without the connection status
export const youtubeSimpleFetcher = async (url: string): Promise<UIPost[]> => {
  try {
    const response = await youtubeFetcher(url);
    // Ensure we always return an array, even if the response data is undefined
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    logger.error('YouTube fetcher error:', error);
    return [];
  }
};

// Helper function to find comment and ad details in FacebookAdAccount data
export function findCommentDetails(accounts: FacebookAdAccount[], commentId: string) {
  for (const account of accounts) {
    for (const ad of account.ads) {
      const comment = ad.comments.find(c => c.id === commentId);
      if (comment) {
        return {
          comment,
          adId: ad.id,
          pageId: ad.creative?.object_story_spec?.page_id
        };
      }
    }
  }
  throw new Error('Comment not found');
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getClientIp(request: NextRequest): string {
  const xForwardedFor = request.headers.get('x-forwarded-for');
  if (xForwardedFor) {
    return xForwardedFor.split(',')[0].trim();
  }
  return request.headers.get('cf-connecting-ip') || 
         request.headers.get('x-real-ip') || 
         '127.0.0.1';
}

// Helper function to check if a plan is a complete package plan
export function isCompletePackagePlan(planType: string | undefined | null): boolean {
  return planType === 'complete_package';
}

interface CheckAiReplyLimitResult {
  canGenerate: boolean;
  currentCount: number;
  limit: number;
  error?: string;
}

export async function checkAiReplyLimit(
  userId: string,
  stripeSubscriptionId: string | null | undefined,
  prisma: any
): Promise<CheckAiReplyLimitResult> {
  const user = await prisma.user.findUnique({
    where: { id: userId },
    select: {
      aiReplyCount: true,
      aiReplyResetDate: true,
    },
  });

  if (!user) {
    return {
      canGenerate: false,
      currentCount: 0,
      limit: 0,
      error: 'User not found',
    };
  }

  const limit = stripeSubscriptionId 
    ? parseInt(process.env.NEXT_PUBLIC_AI_REPLY_LIMIT_COMPLETE || '2000')
    : parseInt(process.env.NEXT_PUBLIC_AI_REPLY_LIMIT_INSTAGRAM || '1000');
  const now = new Date();

  // If reset date is not set or has passed, reset the counter
  if (!user.aiReplyResetDate || user.aiReplyResetDate < now) {
    const nextReset = new Date();
    nextReset.setUTCMonth(nextReset.getUTCMonth() + 1);
    nextReset.setUTCDate(1);
    nextReset.setUTCHours(0, 0, 0, 0);

    await prisma.user.update({
      where: { id: userId },
      data: {
        aiReplyCount: 0,
        aiReplyResetDate: nextReset,
      },
    });

    return {
      canGenerate: true,
      currentCount: 0,
      limit,
    };
  }

  // Check if user has exceeded their limit
  if (user.aiReplyCount >= limit) {
    return {
      canGenerate: false,
      currentCount: user.aiReplyCount,
      limit,
      error: 'Monthly AI reply limit reached',
    };
  }

  return {
    canGenerate: true,
    currentCount: user.aiReplyCount,
    limit,
  };
}

export async function incrementAiReplyCount(
  userId: string,
  prisma: any
): Promise<void> {
  await prisma.user.update({
    where: { id: userId },
    data: {
      aiReplyCount: {
        increment: 1,
      },
    },
  });
}
